import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  ContentBox,
  ContentPage,
  NoResults,
  PageActions,
  Pagination,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { usePageHeader } from "@components/ow";
import { ProjectCard, ProjectCardsSkeleton, ProjectGrid } from "../../Common/ProjectCards";

// Página da API por requisição (o padrão do back). As empresas têm poucos projetos;
// a paginação só aparece quando passa de uma página.
const PAGE_SIZE = 50;

const NEW_HREF = "/c/projects/new";
const BREADCRUMB = [{ page: "Projetos", route: "/c/projects" }];

const Pager = styled.div`
  margin-top: 20px;
`;

export default function Projects() {
  const navigate = useNavigate();
  const projectService = useService("projects");
  const isAdmin = JSON.parse(localStorage.getItem("user_ow") || "{}")?.role === "ADM";
  const [loading, setLoading] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);
  const [data, setData] = useState({ list: [], totalRegisters: 0, totalPages: 1 });
  // Só a resposta da última requisição vale (troca de página rápida)
  const lastRequest = useRef(0);

  usePageHeader({ title: "Projetos", breadcrumb: BREADCRUMB });

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex]);

  async function fetchProjects() {
    const request = ++lastRequest.current;
    setLoading(true);
    try {
      // Não-ADM vê só os projetos da Visão Empresa (por "Ver projetos" ou pelos itens de horas)
      const scope = isAdmin ? "" : "as_approver=1&";
      const result = await projectService.list(`${scope}page=${pageIndex + 1}&limit=${PAGE_SIZE}`);
      if (request !== lastRequest.current) return;
      const list = result?.data?.list || [];
      setData({
        list,
        totalRegisters: result?.data?.totalRegisters ?? list.length,
        totalPages: result?.data?.totalPages || 1,
      });
    } catch (error) {
      if (request !== lastRequest.current) return;
      console.error(error);
      toast.error(error?.response?.data?.msg || "Erro ao carregar os projetos");
      setData({ list: [], totalRegisters: 0, totalPages: 1 });
    }
    setLoading(false);
  }

  let content;
  if (loading) {
    content = <ProjectCardsSkeleton />;
  } else if (data.list.length === 0) {
    content = isAdmin ? (
      <NoResults
        notResultText="Nenhum projeto cadastrado"
        includeText="Novo projeto"
        includeAction={() => navigate(NEW_HREF)}
      />
    ) : (
      <NoResults notResultText="Nenhum projeto por aqui" />
    );
  } else {
    content = (
      <>
        <ProjectGrid>
          {data.list.map((project) => (
            <ProjectCard key={project.id} project={project} route={`/c/projects/${project.id}`} />
          ))}
        </ProjectGrid>
        {data.totalPages > 1 ? (
          <Pager>
            <Pagination
              $pageIndex={pageIndex}
              $pageCount={data.totalPages}
              $totalRecords={data.totalRegisters}
              $pageSize={PAGE_SIZE}
              onChangePage={setPageIndex}
            />
          </Pager>
        ) : null}
      </>
    );
  }

  return (
    <ContentPage>
      {isAdmin ? (
        <PageActions>
          <Button $text="Novo projeto" $variant="primary" $action={() => navigate(NEW_HREF)} />
        </PageActions>
      ) : null}
      <ContentBox>{content}</ContentBox>
    </ContentPage>
  );
}
